/**
 * versions-drawer.tsx — "Lịch sử phiên bản" (ngăn kéo bên phải): danh sách bản đã phát hành,
 * xem trước một bản (persona + cài đặt chính), chat thử trên đúng bản đó, khôi phục bản đó về nháp.
 * Hộp "Phát hành" (ghi chú phiên bản) cũng ở đây.
 */
import { useEffect, useRef, useState } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, History, Loader2, RotateCcw, Rocket, SendHorizonal, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/misc'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { apiError } from '@/lib/api-client'
import { currentLocale, i18n } from '@/lib/i18n'
import { createSandboxConversation } from '@/hooks/use-ai'
import { fetchBotVersion, simulateVersionReply, useBotVersions, type BotSnapshot, type BotVersionRow } from './train-api'
import { OUTPUT_LANGUAGES, withDefaults } from './bot-settings'

const fmt = (iso: string) => new Date(iso).toLocaleString(currentLocale(), { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit', year: 'numeric' })

export function VersionsDrawer({ open, onOpenChange, botId, botName, onRestore, canEdit }: {
  open: boolean
  onOpenChange: (o: boolean) => void
  botId: string
  botName: string
  onRestore: (versionId: string, version: number) => Promise<void>
  canEdit: boolean
}) {
  const { t } = useTranslation('aiTrainUi')
  const { data, isLoading } = useBotVersions(open ? botId : undefined)
  const [preview, setPreview] = useState<BotVersionRow | null>(null)

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setPreview(null) }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/30" />
        <DialogPrimitive.Content className="fixed inset-y-0 right-0 z-50 flex w-full max-w-xl flex-col border-l bg-background shadow-xl animate-fade-in">
          <div className="flex items-center gap-2 border-b px-4 py-3">
            {preview && (
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setPreview(null)} aria-label={t('versions.backToList')}><ArrowLeft className="h-4 w-4" /></Button>
            )}
            <DialogPrimitive.Title className="flex flex-1 items-center gap-2 text-base font-semibold">
              <History className="h-4 w-4 text-primary" /> {preview ? t('versions.versionN', { version: preview.version }) : t('versions.title')}
            </DialogPrimitive.Title>
            <DialogPrimitive.Close className="rounded p-1 opacity-70 hover:opacity-100" aria-label={t('common:actions.close')}><X className="h-4 w-4" /></DialogPrimitive.Close>
          </div>
          <DialogPrimitive.Description className="sr-only">{t('versions.srDescription')}</DialogPrimitive.Description>
          <div className="min-h-0 flex-1 overflow-y-auto p-4">
            {preview ? (
              <VersionPreview botId={botId} botName={botName} row={preview} canEdit={canEdit} onRestore={async () => {
                if (!window.confirm(t('versions.confirmRestore', { version: preview.version }))) return
                await onRestore(preview.id, preview.version)
                setPreview(null)
                onOpenChange(false)
              }} />
            ) : isLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> {t('versions.loading')}</div>
            ) : !data?.versions.length ? (
              <p className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
                {t('versions.empty')}
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="py-2 pr-2 font-medium">{t('versions.colVersion')}</th>
                    <th className="py-2 pr-2 font-medium">{t('versions.colNote')}</th>
                    <th className="py-2 pr-2 font-medium">{t('versions.colTime')}</th>
                    <th className="py-2 font-medium">{t('versions.colPublisher')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.versions.map((v) => (
                    <tr key={v.id} className="cursor-pointer border-b align-top hover:bg-muted/40" onClick={() => setPreview(v)}>
                      <td className="py-2 pr-2">
                        <span className="font-medium">v{v.version}</span>
                        {v.isCurrent && <Badge variant="success" className="ml-1 text-[10px]">{t('versions.running')}</Badge>}
                      </td>
                      <td className="max-w-[180px] py-2 pr-2 text-xs">{v.note || <span className="text-muted-foreground">—</span>}</td>
                      <td className="whitespace-nowrap py-2 pr-2 text-xs">{fmt(v.publishedAt)}</td>
                      <td className="py-2 text-xs">{v.publishedByName ?? (v.publishedBy ? '—' : t('versions.system'))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

function VersionPreview({ botId, botName, row, canEdit, onRestore }: {
  botId: string
  botName: string
  row: BotVersionRow
  canEdit: boolean
  onRestore: () => Promise<void>
}) {
  const { t } = useTranslation('aiTrainUi')
  const [snap, setSnap] = useState<BotSnapshot | null>(null)
  const [restoring, setRestoring] = useState(false)
  useEffect(() => {
    let alive = true
    fetchBotVersion(botId, row.id).then((v) => { if (alive) setSnap(v.snapshot) }).catch((err) => toast.error(apiError(err)))
    return () => { alive = false }
  }, [botId, row.id])

  if (!snap) return <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> {t('versions.loading')}</div>
  const s = withDefaults(snap.settings)
  const facts: Array<[string, string]> = [
    [t('versions.factLanguage'), OUTPUT_LANGUAGES.find((l) => l.value === s.outputLanguage)?.label ?? s.outputLanguage],
    [t('versions.factModel'), snap.provider ? `${snap.provider}${snap.model ? ` · ${snap.model}` : ''}` : t('versions.modelDefault')],
    [t('versions.factFast'), s.fastMode ? t('common:status.on') : t('common:status.off')],
    [t('versions.factMemory'), s.memory.enabled ? t('common:status.on') : t('common:status.off')],
    [t('versions.factHumanLike'), s.humanLike.enabled ? t('versions.humanLikeOn', { max: s.humanLike.maxSplit }) : t('common:status.off')],
    [t('versions.factAutoStop'), s.autoStop.enabled ? t('versions.autoStopOn', { groups: s.autoStop.conditionGroups.length, schedules: s.autoStop.schedules.length }) : t('common:status.off')],
    [t('versions.factHandoff'), s.handoff.enabled ? t('versions.handoffOn', { count: s.handoff.intents.filter((i) => i.enabled).length }) : t('common:status.off')],
    [t('versions.factCollect'), t('versions.attributeCount', { count: s.collectAttributes.length })],
  ]
  return (
    <div className="space-y-4">
      <div className="text-xs text-muted-foreground">
        {fmt(row.publishedAt)} · {row.publishedByName ?? t('versions.system')} {row.isCurrent && <Badge variant="success" className="ml-1 text-[10px]">{t('versions.running')}</Badge>}
        {row.note && <p className="mt-1 text-sm text-foreground">{row.note}</p>}
      </div>
      <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 rounded-lg border p-3 text-xs">
        {facts.map(([k, v]) => (<div key={k} className="contents"><dt className="text-muted-foreground">{k}</dt><dd>{v}</dd></div>))}
      </dl>
      <div className="space-y-1">
        <Label className="text-xs">{t('versions.persona')}</Label>
        <pre className="max-h-56 overflow-y-auto whitespace-pre-wrap rounded-md border bg-muted/30 p-3 font-mono text-[11px]">{snap.personaPrompt || t('versions.personaEmpty')}</pre>
      </div>
      <VersionChat botId={botId} botName={botName} versionId={row.id} />
      {canEdit && (
        <Button variant="outline" className="w-full" disabled={restoring} onClick={async () => { setRestoring(true); try { await onRestore() } finally { setRestoring(false) } }}>
          <RotateCcw className="h-4 w-4" /> {t('versions.restore')}
        </Button>
      )}
    </div>
  )
}

type VersionMsg = { role: 'user' | 'bot' | 'system'; text: string }
/** Câu trả lời → các bong bóng (tách như bản nháp, cùng đường `bubbles` của máy chủ). */
function botLines(res: { reply: string | null; bubbles?: string[] }): VersionMsg[] {
  const parts = res.bubbles?.length ? res.bubbles : res.reply ? [res.reply] : []
  return parts.length ? parts.map((text) => ({ role: 'bot', text })) : [{ role: 'system', text: i18n.t('aiTrainUi:versions.noReply') }]
}

/** Chat thử trên một phiên bản đã phát hành (sandbox thật, không ghi vào hội thoại khách). */
function VersionChat({ botId, botName, versionId }: { botId: string; botName: string; versionId: string }) {
  const { t } = useTranslation('aiTrainUi')
  const [msgs, setMsgs] = useState<Array<{ role: 'user' | 'bot' | 'system'; text: string }>>([])
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const convRef = useRef<string | null>(null)

  const send = async () => {
    const text = input.trim()
    if (!text || busy) return
    setInput('')
    setMsgs((m) => [...m, { role: 'user', text }])
    setBusy(true)
    try {
      if (!convRef.current) convRef.current = (await createSandboxConversation(`Thử phiên bản — ${botName}`) // i18n-ignore: tiêu đề hội thoại sandbox lưu vào hệ thống
      ).conversationId
      const res = await simulateVersionReply(convRef.current, text, botId, versionId)
      setMsgs((m) => [...m, ...(res.handoff?.should
        ? [
            ...(res.handoffNotice ? [{ role: 'bot' as const, text: res.handoffNotice }] : []),
            { role: 'system' as const, text: t('versions.handoffTo', { reason: res.handoff.reason ?? '' }) },
          ]
        // Tách bong bóng như bản nháp (cùng đường `bubbles` của máy chủ).
        : botLines(res))])
    } catch (err) {
      setMsgs((m) => [...m, { role: 'system', text: t('versions.error', { message: apiError(err) }) }])
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-2 rounded-lg border p-3">
      <Label className="text-xs">{t('versions.chatTitle')}</Label>
      <div className="max-h-56 min-h-[80px] space-y-1.5 overflow-y-auto rounded-md bg-muted/30 p-2">
        {msgs.length === 0 && <p className="py-4 text-center text-[11px] text-muted-foreground">{t('versions.chatNote')}</p>}
        {msgs.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            {m.role === 'system'
              ? <span className="mx-auto text-[11px] italic text-muted-foreground">{m.text}</span>
              : <div className={`max-w-[85%] whitespace-pre-wrap rounded-lg px-2.5 py-1.5 text-xs ${m.role === 'user' ? 'bg-primary text-primary-foreground' : 'border bg-background'}`}>{m.text}</div>}
          </div>
        ))}
        {busy && <p className="text-[11px] italic text-muted-foreground">{t('versions.composing')}</p>}
      </div>
      <div className="flex gap-2">
        <Input className="h-8 text-xs" value={input} onChange={(e) => setInput(e.target.value)} placeholder={t('versions.chatPlaceholder')}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void send() } }} disabled={busy} />
        <Button size="sm" onClick={() => void send()} disabled={busy || !input.trim()} aria-label={t('common:actions.send')}><SendHorizonal className="h-3.5 w-3.5" /></Button>
      </div>
    </div>
  )
}

export function PublishDialog({ open, onOpenChange, onPublish, hasChanges, neverPublished }: {
  open: boolean
  onOpenChange: (o: boolean) => void
  onPublish: (note: string) => Promise<boolean>
  hasChanges: boolean
  neverPublished: boolean
}) {
  const { t } = useTranslation('aiTrainUi')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  useEffect(() => { if (open) setNote('') }, [open])
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Rocket className="h-4 w-4 text-primary" /> {t('versions.publish')}</DialogTitle>
          <DialogDescription>
            {t('versions.publishDesc')}
            {!hasChanges && !neverPublished && t('versions.publishNoChange')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1">
          <Label className="text-xs">{t('versions.noteLabel')} <span className="font-normal text-muted-foreground">{t('versions.optional')}</span></Label>
          <Textarea rows={3} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('versions.notePlaceholder')} />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{t('common:actions.cancel')}</Button>
          <Button disabled={busy} onClick={async () => {
            setBusy(true)
            try { if (await onPublish(note.trim())) onOpenChange(false) } finally { setBusy(false) }
          }}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />} {t('versions.publish')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
